import React, { useState, useEffect } from "react";
import { Container, Form, Modal, Spinner, Table } from "react-bootstrap";
import "../css/InfoModal.css";
import Icon from "../../generic/Icon";
import { useDrag, useDrop } from "react-dnd";
import useApi from "../../../apiProvider";
import styles from "../../generic/css/Table.module.css";
import { useSiteContext } from "../Providers/SSCProvider";

const ItemTypes = {
  CIRCUITMEMBER: "circuitMember",
};

const DEFAULT_GROUP_ID = 1;

const DiverseModal = ({ show, setShow }) => {
  const closeModal = () => setShow(false);
  const { selectedSite, rebuildHierarchy, isLoading, setIsLoading } =
    useSiteContext();

  const [draftGroups, setDraftGroups] = useState([]);
  const [originalGroups, setOriginalGroups] = useState([]);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 7500);
  };

  useEffect(() => {
    if (!show || !selectedSite?.diversityGroups?.length) return;

    const groupsWithMembers = selectedSite.diversityGroups.map((group) => ({
      groupId: group.groupId, // BACKEND CONTRACT
      siteId: selectedSite.siteId,
      name: group.name,
      reporting: group.reporting,
      circuitMembers: group.circuits.map((circuit) => ({
        circuitGmId: circuit.circuitGmId,
        name: circuit.circuitVendorId,
        carrier: circuit.carrier,
        lec: circuit.lec,
        structureId: circuit.structureId,
      })),
    }));

    const cloned = JSON.parse(JSON.stringify(groupsWithMembers));
    setDraftGroups(cloned);
    setOriginalGroups(cloned);
  }, [show, selectedSite]);

  // Track Ctrl key
  useEffect(() => {
    const handleKeyDown = (e) => e.ctrlKey && setIsCtrlPressed(true);
    const handleKeyUp = (e) => !e.ctrlKey && setIsCtrlPressed(false);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", () => setIsCtrlPressed(false));
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", () => setIsCtrlPressed(false));
    };
  }, []);

  // Detect changes
  useEffect(() => {
    if (!originalGroups.length) return;
    setHasChanges(
      JSON.stringify(draftGroups) !== JSON.stringify(originalGroups)
    );
  }, [draftGroups, originalGroups]);

  const newDiversityGroup = () => {
    setDraftGroups((prev) => [
      ...prev,
      {
        groupId: Date.now(),
        siteId: selectedSite?.siteId,
        name: "",
        reporting: false,
        circuitMembers: [],
      },
    ]);
  };

  const handleSave = async () => {
    setIsLoading(true);

    if (draftGroups.some((g) => !g.name?.trim())) {
      showError("Group Name Required");
      setIsLoading(false);
      return;
    }

    // const payload = draftGroups.map((group) => ({
    //   groupId: group.groupId,
    //   siteId: group.siteId,
    //   name: group.name,
    //   reporting: group.reporting,
    //   circuitsIds: group.circuitMembers.map((m) => m.circuitGmId),
    // }));

    const groupsToUpdate = draftGroups.map((group) => ({
      ...group,
      circuitsIds: group.circuitMembers?.map((m) => m.circuitGmId) || [],
    }));

    const results = await useApi.updateDiversityGroups(groupsToUpdate);
    console.log("results: ", results);

    rebuildHierarchy({
      groups: results?.returnData.diversityGroups,
      assignments: results?.returnData.diversityGroupAssignments,
    });

    setOriginalGroups(JSON.parse(JSON.stringify(draftGroups)));
    closeModal();
    setIsLoading(false);
  };

  return (
    <Container>
      <Modal
        show={show}
        onHide={closeModal}
        size="lg"
        dialogClassName="info-modal"
      >
        {isLoading && (
          <div className={styles.overlay}>
            <Spinner animation="border" variant="light" />
          </div>
        )}

        <Modal.Header
          style={{ border: "none", flexShrink: 0 }}
          className="ms-2 me-2"
        >
          <Modal.Title
            style={{
              color: "navy",
              textAlign: "center",
              width: "100%",
              fontWeight: "bold",
            }}
          >
            Carrier Diversity Groups
          </Modal.Title>
        </Modal.Header>

        <div
          style={{
            padding: "0 1rem",
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              fontWeight: "600",
              fontSize: "1rem",
              textAlign: "center",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            Site: {selectedSite?.siteName} ({selectedSite?.siteId})
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Icon name="add" onClick={newDiversityGroup} />
            <Icon name="discard" onClick={closeModal} />
            <Icon
              name="save"
              onClick={handleSave}
              overrides={{
                style: {
                  opacity: hasChanges ? 1 : 0.5,
                  pointerEvents: hasChanges ? "auto" : "none",
                },
              }}
            />
          </div>
        </div>

        <div
          style={{
            color: "red",
            padding: "0 1rem",
            display: "flex",
            justifyContent: "center",
            minHeight: "1.5rem",
            visibility: error ? "visible" : "hidden",
          }}
        >
          {error || ""}
        </div>

        <Modal.Body
          style={{
            overflowY: "auto",
            flex: 1,
            padding: "1rem",
            paddingTop: 0,
          }}
        >
          <Table striped bordered hover>
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: "center",
                    padding: "8px",
                    verticalAlign: "middle",
                  }}
                >
                  Name
                </th>
                <th
                  style={{
                    textAlign: "center",
                    padding: "8px",
                    verticalAlign: "middle",
                  }}
                >
                  Report
                </th>
                <th
                  style={{
                    padding: 0,
                    background: "#f8f9fa",
                    verticalAlign: "middle",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                      paddingLeft: 8,
                      height: "36px",
                    }}
                  >
                    <div style={{ width: 32 }}></div>
                    <div
                      style={{ flex: 1, padding: "0 8px", textAlign: "center" }}
                    >
                      Circuit
                    </div>
                    <div
                      style={{ flex: 1, padding: "0 8px", textAlign: "center" }}
                    >
                      Carrier
                    </div>
                    <div
                      style={{ flex: 1, padding: "0 8px", textAlign: "center" }}
                    >
                      LEC
                    </div>
                    <div style={{ width: 50 }}></div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {draftGroups
                .slice()
                .sort((a, b) => (a.groupId ?? 0) - (b.groupId ?? 0))
                .map((group) => (
                  <tr key={group.groupId}>
                    <GroupRow
                      group={group}
                      groups={draftGroups}
                      setGroups={setDraftGroups}
                      isCtrlPressed={isCtrlPressed}
                      showError={showError}
                    />
                  </tr>
                ))}
            </tbody>
          </Table>
          <div style={{ marginTop: 8, fontStyle: "italic", fontSize: 12 }}>
            Tip: Drag to move members. Hold Ctrl while dropping to copy.
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

const GroupRow = ({ group, groups, setGroups, isCtrlPressed, showError }) => {
  const updateGroup = (updates) =>
    setGroups((prev) =>
      prev.map((g) => (g.groupId === group.groupId ? { ...g, ...updates } : g))
    );

  return (
    <>
      <td>
        <input
          className="form-control"
          value={group.name}
          onChange={(e) => updateGroup({ name: e.target.value })}
          placeholder="*"
        />
        {group.id}
        {group.groupId === DEFAULT_GROUP_ID && (
          <span
            style={{
              display: "block",
              marginTop: "2px",
              marginLeft: "2px",
              fontStyle: "italic",
              fontSize: "0.65rem",
              color: "#888",
              textAlign: "left",
            }}
          >
            Default Group
          </span>
        )}
      </td>
      <td>
        <Form.Check
          type="switch"
          id="custom-switch"
          label=""
          checked={group.reporting}
          onChange={(e) => updateGroup({ reporting: e.target.checked })}
          style={{ display: "inline-block" }}
        />
      </td>
      <GroupColumn
        group={group}
        groups={groups}
        setGroups={setGroups}
        isCtrlPressed={isCtrlPressed}
        showError={showError}
      />
    </>
  );
};

const GroupColumn = ({
  group,
  groups,
  setGroups,
  isCtrlPressed,
  showError,
}) => {
  const [, drop] = useDrop({
    accept: ItemTypes.CIRCUITMEMBER,
    drop: (item) => {
      if (item.groupId === group.groupId) return;

      setGroups((prevGroups) => {
        let newGroups = prevGroups;
        if (!isCtrlPressed) {
          newGroups = prevGroups.map((g) =>
            g.groupId === item.groupId
              ? {
                  ...g,
                  circuitMembers: g.circuitMembers.filter(
                    (m) => m.circuitGmId !== item.circuitGmId
                  ),
                }
              : g
          );
        }

        return newGroups.map((g) =>
          g.groupId === group.groupId &&
          !g.circuitMembers.some((m) => m.circuitGmId === item.circuitGmId)
            ? { ...g, circuitMembers: [...g.circuitMembers, item] }
            : g
        );
      });
    },
  });

  return (
    <td
      ref={drop}
      style={{
        minWidth: 250,
        verticalAlign: "top",
        padding: 0,
        background: "#f8f9fa",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {group.circuitMembers?.length === 0 && (
          <div style={{ padding: "8px 0 8px 8px" }}>
            <small style={{ color: "#888" }}>Drop circuits here</small>
            <br />
            {group.groupId === DEFAULT_GROUP_ID ? (
              <small style={{ color: "#888" }}>
                Default Group cannot be deleted
              </small>
            ) : (
              <small style={{ color: "red" }}>
                Empty Groups will be deleted nightly
              </small>
            )}
          </div>
        )}
        {group.circuitMembers?.map((member) => (
          <CircuitMember
            key={member.circuitGmId}
            member={member}
            groupId={group.groupId}
            setGroups={setGroups}
            showError={showError}
          />
        ))}
      </div>
    </td>
  );
};

const CircuitMember = ({ member, groupId, setGroups, showError }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CIRCUITMEMBER,
    item: { ...member, groupId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleRemove = () => {
    setGroups((prevGroups) => {
      const groupsContaining = prevGroups.filter((g) =>
        g.circuitMembers.some((m) => m.circuitGmId === member.circuitGmId)
      );

      if (groupsContaining.length > 1) {
        return prevGroups.map((g) =>
          g.groupId === groupId
            ? {
                ...g,
                circuitMembers: g.circuitMembers.filter(
                  (m) => m.circuitGmId !== member.circuitGmId
                ),
              }
            : g
        );
      } else {
        showError(
          `Cannot remove member ${member.name} because it only exists in one group`
        );
      }

      return prevGroups;
    });
  };

  return (
    <div
      ref={drag}
      style={{
        display: "flex",
        alignItems: "center",
        opacity: isDragging ? 0.5 : 1,
        padding: "0 0 0 8px",
        margin: 0,
        minHeight: 36,
        borderBottom: "1px solid #e0e0e0",
        backgroundColor: "#e1ecf7",
        borderRadius: 0,
        cursor: "move",
        userSelect: "none",
      }}
    >
      <div style={{ width: 32, display: "flex", alignItems: "center" }}>
        <Icon
          name="moveBars"
          overrides={{
            tooltipPlacement: "top",
            size: "1rem",
            style: { cursor: "move" },
          }}
        />
      </div>
      <div style={{ flex: 1, display: "flex", width: "100%" }}>
        <div
          style={{
            flex: 1,
            padding: "0 8px",
            minWidth: 0,
            fontWeight: 500,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {member.name}
        </div>
        <div
          style={{
            flex: 1,
            padding: "0 8px",
            minWidth: 0,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {member.carrier}
        </div>
        <div
          style={{
            flex: 1,
            padding: "0 8px",
            minWidth: 0,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {member.lec}
        </div>
      </div>
      <div
        style={{
          width: 50,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingRight: 8,
        }}
      >
        <Icon
          name="remove"
          onClick={handleRemove}
          overrides={{
            tooltip: "Remove Circuit From Group",
            tooltipPlacement: "top",
            size: ".6rem",
            style: { cursor: "pointer" },
          }}
        />
      </div>
    </div>
  );
};

export default DiverseModal;
